import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { fieldsTypes } from 'config/constants';
import RuleFields from 'components/SlateViewsSettings/RuleFields/RuleFields';
import 'components/FieldSelector/FieldSelector.css';

const DateFieldSelector = ({ currentSUUID, fields, onChange, errorText, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const { t } = useTranslation();

  const dateFields = useMemo(
    function() {
      return fields
        .filter(function(field) { return field.type === fieldsTypes.DATETIME; })
        .map(function(field) { return { ...field, parent: undefined, children: undefined }; });
    },
    [fields],
  );

  const currentField = dateFields.find(
    function(field) { return field.suuid === currentSUUID; },
  );

  var updateDropdownPosition = useCallback(function() {
    if (triggerRef.current) {
      var rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  var handleClickOutside = useCallback(function(event) {
    var trigger = triggerRef.current;
    var dropdown = dropdownRef.current;
    var clickedInTrigger = trigger && trigger.contains(event.target);
    var clickedInDropdown = dropdown && dropdown.contains(event.target);

    if (!clickedInTrigger && !clickedInDropdown) {
      setIsOpen(false);
    }
  }, []);

  useEffect(function() {
    document.addEventListener('mousedown', handleClickOutside);
    return function() {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Close dropdown when the page scrolls or user scrolls outside the dropdown
  useEffect(function() {
    if (!isOpen) return;
    var handlePageScroll = function(e) {
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setIsOpen(false);
    };
    window.addEventListener('scroll', handlePageScroll, true);
    window.addEventListener('wheel', handlePageScroll, true);
    return function() {
      window.removeEventListener('scroll', handlePageScroll, true);
      window.removeEventListener('wheel', handlePageScroll, true);
    };
  }, [isOpen]);

  var handleClick = function(e) {
    if (e) {
      e.preventDefault();
    }
    if (disabled) {
      return;
    }
    if (!isOpen) {
      updateDropdownPosition();
    }
    setIsOpen(!isOpen);
  };

  var handleFieldChange = function(field) {
    setIsOpen(false);
    onChange(field.suuid, field.title);
  };

  var onRemoveRule = function(e) {
    if (e) {
      e.stopPropagation();
    }
    if (disabled) {
      return;
    }
    onChange('', '');
  };

  var containerClasses = ['field-selector'];
  if (isOpen) containerClasses.push('field-selector--open');
  if (disabled) containerClasses.push('field-selector--disabled');
  if (errorText) containerClasses.push('field-selector--error');

  return (
    <div className={containerClasses.join(' ')}>
      <button
        ref={triggerRef}
        type="button"
        className="field-selector-trigger"
        onClick={handleClick}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={'field-selector-value' + (!currentField ? ' field-selector-value--placeholder' : '')}>
          {currentField ? currentField.title : t('settings:selectField')}
        </span>
        <span className={'field-selector-chevron' + (isOpen ? ' field-selector-chevron--open' : '')}>
          <span className="material-symbols-outlined">expand_more</span>
        </span>
      </button>
      {currentField && (
        <button
          type="button"
          className="field-selector-remove"
          onClick={onRemoveRule}
          disabled={disabled}
          aria-label={t('btn:remove')}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      )}
      {errorText && (
        <div className="field-selector-error">{errorText}</div>
      )}
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="field-selector-portal"
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: dropdownPosition.width,
            zIndex: 9999,
          }}
        >
          <RuleFields fields={dateFields} onFieldClick={handleFieldChange} />
        </div>,
        document.body
      )}
    </div>
  );
};

DateFieldSelector.propTypes = {
  currentSUUID: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  errorText: PropTypes.string,
  disabled: PropTypes.bool,
};

export default DateFieldSelector;
